import React, { SetStateAction, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonGroup } from 'reactstrap';
import cx from 'classnames';

import Button from 'src/components/core/Button/Button';
import Toggle from 'src/components/core/Toggle/Toggle';
import EditableTranscript from 'src/components/EditableTranscript/EditableTranscript';
import InteractiveTranscript from 'src/components/InteractiveTranscript/InteractiveTranscript';
import Panel from 'src/components/Panel/Panel';
import { useSeekTime } from 'src/contexts/GlobalAudioContext';
import { Conversation } from 'src/types/conversation';
import { slateSnippetSelector } from 'src/util/slate';
import { getTranscript } from '../../../api/api';

import styles from '../ConversationRoute.module.scss';
import tabStyles from './TranscriptTab.module.scss';

const FONT_SIZES = [
  'medium',
  'large',
  'larger',
  'x-large',
  'xx-large',
  'xxx-large',
] as const;

interface TranscriptTabProps {
  isActive: boolean;
  autoScroll: boolean;
  conversation: Conversation;
  handlePlay: (seekTime?: number, endTime?: number) => void;
  showEditableTranscript: boolean;
  isStaff: boolean;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  setAutoScroll: React.Dispatch<SetStateAction<boolean>>;
}

const TranscriptTab = ({
  isActive,
  autoScroll,
  conversation,
  handlePlay,
  showEditableTranscript,
  isStaff,
  scrollContainerRef,
  setAutoScroll,
}: TranscriptTabProps) => {
  const { t } = useTranslation();
  const [fontSizeIndex, setFontSizeIndex] = React.useState(0);
  const [editTranscript, setEditTranscript] = React.useState(false);
  const transcriptHeaderRef = React.useRef<HTMLDivElement | null>(null);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const prevScrollRatio = React.useRef(0);

  useAutoScroll(isActive, editTranscript, autoScroll);

  useLayoutEffect(() => {
    // when editTranscript changes, maintain scroll position using ratio
    if (!scrollContainerRef?.current) return;

    const container = scrollContainerRef.current;
    const newScrollTop = container.scrollHeight * prevScrollRatio.current;
    container.scrollTop = newScrollTop;
  }, [editTranscript, scrollContainerRef]);

  const toggleTranscriptMode = () => {
    if (scrollContainerRef?.current) {
      const container = scrollContainerRef.current;

      // Ratio of scrollTop to container height
      prevScrollRatio.current =
        container.scrollHeight > 0
          ? container.scrollTop / container.scrollHeight
          : 0;
    }
    setEditTranscript((et) => !et);
  };

  const handleDownload = () => {
    setIsDownloading(true);
    fetchTranscript();
  };

  const fetchTranscript = async () => {
    try {
      const response = await getTranscript(conversation.id);
      if (response.ok) {
        const contentType = response.headers.get('content-type');

        // decipher content-type
        if (contentType && contentType.includes('text/plain')) {
          const text = await response.text();

          downloadTranscript(text);
        } else if (contentType && contentType.includes('application/json')) {
          const json = await response.json();
          const errMsg = json.data.error;

          setIsDownloading(false);
          throw new Error(errMsg);
        } else {
          setIsDownloading(false);
          throw new Error('Unexpected content type');
        }
      } else {
        setIsDownloading(false);
        throw new Error(`Request Failed. Status: ${response.status}`);
      }
    } catch (error) {
      setIsDownloading(false);
      alert(error);
    }
  };

  const downloadTranscript = (response: string) => {
    const blob = new Blob([response], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `transcript_${conversation.id}.txt`;
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(link);
    setIsDownloading(false);
  };

  const handleIncrease = () => {
    if (fontSizeIndex < FONT_SIZES.length - 1) {
      setFontSizeIndex(fontSizeIndex + 1);
    }
  };
  const handleDecrease = () => {
    if (fontSizeIndex > 0) {
      setFontSizeIndex(fontSizeIndex - 1);
    }
  };
  const redactOnly = isStaff && conversation.source_id === 'app'; // Staff can get to editing mode on Mobile conversations
  const readyToEdit =
    (conversation.transcript_edit_version != null &&
      conversation.speech_pipeline_status === 8) ||
    redactOnly;

  return (
    <>
      <div
        className={cx(styles.headingContainer, {
          [styles.headingEditing]: editTranscript,
        })}
      >
        <div className="d-flex w-50">
          <h4 className="m-0" ref={transcriptHeaderRef}>
            {t('conversation.transcript')}
          </h4>
        </div>
        <div className="d-flex w-50 justify-content-end align-items-stretch">
          <Toggle
            checked={autoScroll}
            handleChange={() => setAutoScroll((as) => !as)}
            className={cx(styles.autoScrollToggle)}
            label={t('common.autoScroll')}
            id="auto-scroll-toggle"
          />

          {showEditableTranscript && (
            <Button
              color="primary"
              outline={!editTranscript}
              grayOutline={!editTranscript}
              whiteBg={!editTranscript}
              disabled={!readyToEdit}
              icon={['fas', 'pen']}
              className={`mx-1 align-self-stretch`}
              onClick={toggleTranscriptMode}
              title={readyToEdit ? undefined : t('transcript_editor.not_ready')}
              id="edit-transcript-button"
              data-testid="edit-transcript-button"
            >
              {editTranscript ? `${t('common.editing')}` : t('common.edit')}
            </Button>
          )}
          <Button
            color="primary"
            grayOutline={true}
            whiteBg={true}
            icon={isDownloading ? ['far', 'loader'] : ['fas', 'download']}
            className={`mx-1 align-self-stretch`}
            onClick={handleDownload}
            data-testid="download-transcript-button"
            disabled={isDownloading}
          >
            {isDownloading ? 'Downloading' : 'Download'}
          </Button>
          <ButtonGroup
            className={cx('rounded-start', styles.fontSizeButtonGroup)}
          >
            <span className={`${styles.fontSizeSpan}`}>
              {t('conversation.font_size_btn')}
            </span>
            <Button
              color="primary"
              aria-label="Decrease font size"
              onClick={handleDecrease}
              icon={['far', 'minus']}
              disabled={fontSizeIndex === 0}
              className={styles.fontSizeLeftButton}
            />
            <Button
              color="primary"
              aria-label="Increase font size"
              onClick={handleIncrease}
              icon={['far', 'plus']}
              disabled={fontSizeIndex === FONT_SIZES.length - 1}
            />
          </ButtonGroup>
        </div>
      </div>
      <Panel
        p={null}
        className={`${tabStyles[`word-${FONT_SIZES[fontSizeIndex]}`]} ${
          tabStyles.transcriptPanel
        }`}
      >
        <div className={cx({ 'd-none': !editTranscript })}>
          {showEditableTranscript && readyToEdit && (
            <EditableTranscript
              snippets={conversation.snippets || []}
              ref={scrollContainerRef}
              redactOnly={redactOnly}
              isStaff={isStaff}
              setAutoScroll={setAutoScroll}
            />
          )}
        </div>
        <InteractiveTranscript
          className={cx('pe-0 py-2', { 'd-none': editTranscript })}
          snippets={conversation.snippets || []}
          highlights={conversation.highlights || []}
          conversationId={conversation.id}
          onPlay={handlePlay}
          isActive={isActive}
          isMobile={conversation.source_id === 'app'}
          autoScroll={false}
          conversation={conversation}
          style={{
            border: 'none',
            padding: '16px 8px',
            overflow: 'hidden',
          }}
          innerStyle={{ maxHeight: 'unset', overflow: 'unset' }}
        />
      </Panel>
    </>
  );
};

export default TranscriptTab;

// HELPERS

function useAutoScroll(
  isActive: boolean,
  isEditing: boolean,
  autoScroll: boolean
) {
  const seekTime = useSeekTime({ isActive });

  React.useEffect(() => {
    if (!seekTime || !autoScroll) return;

    let snippetSelector = '.is-seek-chunk';
    if (isEditing) {
      // EditableTranscript labels snippets with different selectors,
      // so we gotta switch to those here when in edit mode
      snippetSelector = slateSnippetSelector(seekTime);
    }

    document
      .querySelector(snippetSelector)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [autoScroll, isEditing, seekTime]);
}

// DEFINITIONS

const AUDIO_RANGE_INTERVAL = 100;
